import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { fetchDealReasons } from './dealApi'

/**
 * Закрытие сделки с причиной из справочника (тикет #100) — вместо `window.prompt`.
 *
 * Причина выбирается, а не набирается: отчёт о причинах (ТЗ §4.6) группирует по ней, и
 * набранный текст дробил одну находку («проигрываем по цене») на десяток написаний, каждое
 * слишком мелкое, чтобы его заметить. Комментарий остаётся — но рядом с причиной, а не
 * вместо неё.
 *
 * Для проигрыша причина обязательна — кнопка неактивна, пока её нет; для выигрыша —
 * нет: отказываться от продажи из-за пустого ответа «почему выиграли» никто не станет.
 * Сервер проверяет то же самое сам; здесь — только честная кнопка.
 */
export function CloseDealForm({
  kind,
  title,
  pending,
  onConfirm,
  onCancel,
}: {
  kind: 'lost' | 'won'
  title: string
  pending?: boolean
  onConfirm: (reasonId: string | null, note: string | null) => void
  onCancel: () => void
}) {
  const { t } = useApp()
  const [reasonId, setReasonId] = useState('')
  const [note, setNote] = useState('')

  const reasons = useQuery({
    queryKey: ['deal-reasons', kind],
    queryFn: ({ signal }) => fetchDealReasons(kind, signal),
    staleTime: 5 * 60_000,
  })
  // Retired reasons stay readable on closed deals but are not offered for a new close.
  const offered = (reasons.data ?? []).filter((reason) => reason.active)
  const required = kind === 'lost'

  return (
    <div className="notice card-gap" role="dialog" aria-label={t(`deal.close.${kind}`)}>
      <p>
        <strong>{t(`deal.close.${kind}`)}</strong>: {title}
      </p>
      {reasons.isLoading && <p>{t('app.loading')}</p>}
      {reasons.error && <p role="alert">{t('app.error')}</p>}
      <label>
        {t('deal.close.reason')}{' '}
        <select value={reasonId} onChange={(event) => setReasonId(event.target.value)}>
          <option value="">{required ? t('deal.close.choose') : t('deal.close.noReason')}</option>
          {offered.map((reason) => (
            <option key={reason.id} value={reason.id}>
              {reason.name}
            </option>
          ))}
        </select>
      </label>
      {kind === 'lost' && (
        <label>
          {t('deal.close.note')}{' '}
          <input value={note} maxLength={255} onChange={(event) => setNote(event.target.value)} />
        </label>
      )}
      <div className="report-actions">
        <button
          type="button"
          disabled={pending || (required && reasonId === '')}
          onClick={() => onConfirm(reasonId === '' ? null : reasonId, note.trim() === '' ? null : note.trim())}
        >
          {t('deal.close.confirm')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('card.cancel')}
        </button>
      </div>
    </div>
  )
}
